import { FC, useContext, useState } from 'react';

import { useMutation } from '@apollo/client';
import {
  Button,
  H2,
  Modal,
  Paragraph,
  TimeForwardIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { HOSTS_DATA } from 'hooks/useHostsData/schema';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import { PostPaymentModalContent } from '../PostPaymentsModal';
import {
  IStopClientSessionModalProps,
  IStopClientSessionRequest,
  IStopClientSessionResponse,
} from './intefaces';
import { STOP_CLIENT_SESSION } from './schema';
import s from './StopClientSessionModal.module.scss';

export const StopClientSessionModal: FC<IStopClientSessionModalProps> = ({
  closeModal,
  sessionId,
  isPostPayment,
  refetch,
  stopPolling,
  statedPoling,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [type, setType] = useState<'stop_client_session' | 'post_payment'>(
    'stop_client_session'
  );

  const [stopClientSession, { loading, data }] = useMutation<
    IStopClientSessionResponse,
    IStopClientSessionRequest
  >(STOP_CLIENT_SESSION, {
    update: (cache, { data }) => {
      const session = data?.stopClientSession;

      if (!session) return;

      cache.evict({ id: cache.identify(session) });
      cache.gc();
    },
    onCompleted: (data) => {
      if (!data.stopClientSession) return;
    },
    onError: console.error,
    refetchQueries: isPostPayment ? undefined : [HOSTS_DATA],
  });

  if (isPostPayment && stopPolling) {
    stopPolling();
  }

  return (
    <Modal
      onClose={() => {
        if (isPostPayment && statedPoling) {
          statedPoling();
        }
        closeModal();
      }}
      className={cn(
        type === 'stop_client_session' && s.container,
        isPostPayment && s.container_stopPostPayment,
        type === 'post_payment' && s.container_postPayment
      )}
      disableCloseByEsc={type === 'post_payment'}
      disableOutsideClose={type === 'post_payment'}
      hideCloseIcon={type === 'post_payment'}
    >
      {type === 'stop_client_session' ? (
        <>
          {isPostPayment ? (
            <>
              <TimeForwardIcon size="xxl" color="secondary" />
              <div className={s.info}>
                <H2>
                  {t(
                    `session.post_payment.finish.title.${
                      permissions?.has('payment_create')
                        ? 'isActiveOperator'
                        : 'isNotActiveOperator'
                    }`
                  )}
                </H2>
                <Paragraph color="secondary">
                  {t(
                    `session.post_payment.finish.description.${
                      permissions?.has('payment_create')
                        ? 'isActiveOperator'
                        : 'isNotActiveOperator'
                    }`
                  )}
                </Paragraph>
              </div>

              <div className={s.buttons}>
                <Button
                  fullWidth
                  isLoading={loading}
                  className={s.button}
                  onClick={() => {
                    stopClientSession({
                      variables: { id: sessionId, ignorePause: false },
                    }).then((data) => {
                      if (
                        !data ||
                        !data.data?.stopClientSession.payment ||
                        !permissions?.has('payment_create')
                      ) {
                        notification.info({
                          t,
                          type: 'info',
                          title: t('host.notification.seanceFinished'),
                          message:
                            !data || !data.data?.stopClientSession.payment
                              ? t('session.post_payment.not_payment.message')
                              : undefined,
                          leftDescription:
                            data.data?.stopClientSession.hosts[0].host.alias,
                          dismiss: 3,
                        });
                        refetch();
                        statedPoling && statedPoling();
                        closeModal();

                        return;
                      }

                      setType('post_payment');
                    });
                  }}
                >
                  {t(
                    `session.post_payment.finish.confirm.${
                      permissions?.has('payment_create')
                        ? 'isActiveOperator'
                        : 'isNotActiveOperator'
                    }`
                  )}
                </Button>
                <Button
                  fullWidth
                  onClick={closeModal}
                  variant="ghost"
                  className={s.button_cancel}
                >
                  {t('session.post_payment.finish.cancel')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <header>{t('session.finish')}</header>
              <Button
                fullWidth
                isLoading={loading}
                className={s.button}
                onClick={() => {
                  stopClientSession({
                    variables: { id: sessionId, ignorePause: false },
                  }).then(() => {
                    notification.info({
                      t,
                      type: 'info',
                      title: t('host.notification.seanceFinished'),
                      leftDescription:
                        data?.stopClientSession.hosts[0].host.alias,
                      dismiss: 3,
                    });
                    closeModal();
                  });
                }}
              >
                {t('confirm')}
              </Button>
            </>
          )}
        </>
      ) : data?.stopClientSession.payment ? (
        <PostPaymentModalContent
          payment={data?.stopClientSession.payment}
          onClose={closeModal}
          type="host"
          hostName={data?.stopClientSession.hosts[0].host.alias}
          refetch={refetch}
          statedPoling={statedPoling}
        />
      ) : null}
    </Modal>
  );
};
